/**
 * src/data/productDetails.ts
 *
 * Per-product editorial content: scent notes, scent profile, texture,
 * badges, and accordion sections.
 *
 * Keyed by product handle (matches Product.handle in products.ts).
 * This data will eventually come from a CMS or Shopify metafields.
 *
 * TEMPLATES:
 * - Fragrance: Use fragranceTemplate() below to standardize all fragrances
 * - Body Product: Use bodyProductTemplate() below to standardize body items
 */

import { FragranceNotes, ScentProfileEntry } from '../types/fragrance';

export interface ProductDetail {
  /** Full top/heart/base pyramid — fragrances only */
  notes?: FragranceNotes;
  /** Dot-bar intensity ratings — fragrances only */
  scentProfile?: ScentProfileEntry[];
  /** Simple one-line scent description — body products */
  scent?: string;
  /** Texture description — body products */
  texture?: string;
  /** Short trust signals shown below the ATC button */
  badges: string[];
  /** Expandable detail sections */
  accordionItems: { title: string; content: string }[];
}

/**
 * TEMPLATE: Fragrance Product
 * Use this when adding a new fragrance. Just fill in the blanks.
 */
export function fragranceTemplate(options: {
  noteTopFull: string;     // e.g., 'Fresh Rose • Egyptian Jasmine'
  noteHeartFull: string;   // e.g., 'Velvety Rose Absolute • Warm Benzoin'
  noteBaseFull: string;    // e.g., 'Australian Sandalwood'
  scentProfile: ScentProfileEntry[];  // Array of { label, level }
  ingredients: string;     // Full ingredient list
  badges?: string[];       // Optional, defaults to standard fragrance badges
}): ProductDetail {
  return {
    notes: {
      top: options.noteTopFull,
      heart: options.noteHeartFull,
      base: options.noteBaseFull,
    },
    scentProfile: options.scentProfile,
    badges: options.badges || [
      'Natural Ingredients',
      'Handcrafted in Small Batches',
      'Vegan & Cruelty Free',
    ],
    accordionItems: [
      {
        title: 'Shipping & Returns',
        content: 'Orders ship within 3–5 business days. We accept returns on unopened products within 14 days of delivery.',
      },
      {
        title: 'Ingredients',
        content: options.ingredients,
      },
      {
        title: 'The Process',
        content: 'Each fragrance is blended by hand in small batches. Formulas are developed over weeks of testing to ensure the scent evolves beautifully on the skin.',
      },
    ],
  };
}

/**
 * TEMPLATE: Body Product
 * Use this when adding a new body product (oil, butter, etc). Just fill in the blanks.
 */
export function bodyProductTemplate(options: {
  scent: string;           // One-line scent description
  texture: string;         // Texture description
  howToUse: string;        // Usage instructions
  ingredients: string;     // Full ingredient list
  badges?: string[];       // Optional, defaults to standard body badges
}): ProductDetail {
  return {
    scent: options.scent,
    texture: options.texture,
    badges: options.badges || [
      'Natural Ingredients',
      'Handcrafted in Small Batches',
      'Vegan & Cruelty Free',
    ],
    accordionItems: [
      {
        title: 'How to Use',
        content: options.howToUse,
      },
      {
        title: 'Ingredients',
        content: options.ingredients,
      },
      {
        title: 'Shipping & Returns',
        content: 'Orders ship within 3–5 business days. We accept returns on unopened products within 14 days of delivery.',
      },
    ],
  };
}

const productDetails: Record<string, ProductDetail> = {
  /* ── Fragrances ──────────────────────────────────────────────────────────── */
  'vinyl-rose-perfume-oil': fragranceTemplate({
    noteTopFull: 'Fresh Rose • Egyptian Jasmine',
    noteHeartFull: 'Velvety Rose Absolute • Warm Benzoin',
    noteBaseFull: 'Australian Sandalwood',
    scentProfile: [
      { label: 'Fresh',  level: 2 },
      { label: 'Floral', level: 5 },
      { label: 'Woody',  level: 1.5 },
      { label: 'Warm',   level: 4 },
      { label: 'Sweet',  level: 4 },
    ],
    ingredients: 'Fractionated coconut oil, jojoba oil, fragrance blend (rose absolute, jasmine absolute, Australian sandalwood, benzoin resin). All ingredients are thoughtfully sourced.',
  }),

  'canvas-and-cedar-cologne-oil': fragranceTemplate({
    noteTopFull: 'Bright Bergamot',
    noteHeartFull: 'Virginia Cedarwood • Soft Rose',
    noteBaseFull: 'Australian Sandalwood',
    scentProfile: [
      { label: 'Fresh',  level: 4 },
      { label: 'Woody',  level: 5 },
      { label: 'Warm',   level: 4 },
      { label: 'Floral', level: 1 },
      { label: 'Sweet',  level: 1 },
    ],
    ingredients: 'Fractionated coconut oil, jojoba oil, fragrance blend (Virginia cedarwood, Australian sandalwood, bergamot, rose). All ingredients are thoughtfully sourced.',
    badges: ['Natural Ingredients', 'Handcrafted in Small Batches'],
  }),

  /* ── Body ────────────────────────────────────────────────────────────────── */
  'lavender-reverie-body-butter': bodyProductTemplate({
    scent: 'Calming lavender softened by a hint of sweet orange.',
    texture: 'Lightweight whipped shea butter that melts into skin on contact — never greasy, always silky.',
    howToUse: 'Apply generously to skin after bathing. Massage in circular motions until fully absorbed. Best used on slightly damp skin.',
    ingredients: 'Organic shea butter, coconut oil, jojoba oil, vitamin E, lavender essential oil, orange essential oil.',
  }),

  'vinyl-rose-body-oil': bodyProductTemplate({
    scent: 'The warm rose and sandalwood of Vinyl Rose, now in a silky body oil.',
    texture: 'A dry-touch body oil that absorbs quickly without residue.',
    howToUse: 'Apply to damp skin after shower. A few drops go a long way. Can be layered with Vinyl Rose perfume oil.',
    ingredients: 'Jojoba oil, fractionated coconut oil, squalane, fragrance blend.',
    badges: ['Natural Ingredients', 'Handcrafted in Small Batches'],
  }),
};

export const defaultProductDetail: ProductDetail = {
  badges: ['Natural Ingredients', 'Handcrafted in Small Batches'],
  accordionItems: [
    { title: 'Shipping & Returns', content: 'Orders ship within 3–5 business days.' },
    { title: 'Ingredients', content: 'All ingredients are thoughtfully sourced.' },
  ],
};

export default productDetails;
